import { apiClient } from '@/lib/api/client';
import type {
  AuthResponse,
  ChangePasswordRequest,
  ConfirmForgotPasswordRequest,
  ForgotPasswordRequest,
  LoginCredentials,
  NewPasswordChallenge,
  SsoCallbackRequest,
  SsoInitiateRequest,
  SsoInitiateResponse,
} from '../types/auth';

const AUTH_ENDPOINTS = {
  LOGIN: '/auth',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  CURRENT_USER: '/auth/current-user',
  CHALLENGE: '/auth/initial-change-password',
  CHANGE_PASSWORD: '/auth/change-password',
  FORGOT_PASSWORD: '/auth/forgot-password',
  CONFIRM_FORGOT_PASSWORD: '/auth/confirm-forgot-password',
  IS_AUTHENTICATED: '/auth/is-authenticated',
  SSO_INITIATE: '/auth/sso/initiate',
  SSO_CALLBACK: '/auth/sso/callback',
} as const;

export const authService = {
  /**
   * Login with username and password
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>(AUTH_ENDPOINTS.LOGIN, credentials, {
      // Don't retry authentication requests
      retries: 0,
    });
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post<void>(AUTH_ENDPOINTS.LOGOUT);
    } catch (error) {
      // Even if logout fails, we should clear local state
      console.error('Logout error:', error);
    }
  },

  /**
   * Get current user information
   */
  getCurrentUser: async (): Promise<AuthResponse> => {
    return apiClient.get<AuthResponse>(AUTH_ENDPOINTS.CURRENT_USER);
  },

  /**
   * Handle new password challenge
   */
  handleNewPasswordChallenge: async (challenge: NewPasswordChallenge): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>(AUTH_ENDPOINTS.CHALLENGE, challenge, {
      retries: 0,
    });
  },

  /**
   * Change password for authenticated user
   */
  changePassword: async (request: ChangePasswordRequest): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(AUTH_ENDPOINTS.CHANGE_PASSWORD, request, {
      retries: 0,
    });
  },

  /**
   * Forgotpassword flow
   */
  forgotPassword: async (request: ForgotPasswordRequest): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(AUTH_ENDPOINTS.FORGOT_PASSWORD, request, {
      retries: 0,
    });
  },

  /**
   * Confirm forgot password with verification code
   */
  confirmForgotPassword: async (
    request: ConfirmForgotPasswordRequest,
  ): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(AUTH_ENDPOINTS.CONFIRM_FORGOT_PASSWORD, request, {
      retries: 0,
    });
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: async (): Promise<{ message: string }> => {
    return apiClient.get<{ message: string }>(AUTH_ENDPOINTS.IS_AUTHENTICATED);
  },

  /**
   * Start the SSO flow: get the Cognito authorize URL to redirect the browser to.
   * Sets the HTTP-only PKCE verifier cookie server-side.
   */
  ssoInitiate: async (request: SsoInitiateRequest): Promise<SsoInitiateResponse> => {
    return apiClient.post<SsoInitiateResponse>(AUTH_ENDPOINTS.SSO_INITIATE, request, {
      retries: 0,
    });
  },

  /**
   * Complete the SSO flow: exchange the authorization code for tokens.
   * Sets the auth cookies on success and returns the user profile.
   */
  ssoCallback: async (request: SsoCallbackRequest): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>(AUTH_ENDPOINTS.SSO_CALLBACK, request, {
      retries: 0,
    });
  },
};
